import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Button, Icon } from 'tea-component';
import type { IHighPriorityEventItem } from '@op/components/ChartsEvents/types';
import { useSyncState } from '@op/hooks/common';
import CardCommon from '@op/components/CardCommon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';
import { DBO_TM } from '@op/utils/format';
import { ServerTimeType } from '@op/components/FilterCommon/types';

/** 情感分类 */
type SentimentCategory = 'positive' | 'negative';

interface IEventTypeSettingProps {
  /** 高优先级事件数据（原始格式） */
  highPriorityEvents: Record<string, IHighPriorityEventItem[]>;
  /** 弹窗显示状态 */
  visible: boolean;
  /** 当前已选中的事件类型：null 表示全选，数组表示具体选中项 */
  selectedTypes: string[] | null;
  /** 确认回调：返回 null 表示全选，返回数组表示具体选中项 */
  confirm: (selectedTypes: string[] | null) => void;
  /** 关闭回调 */
  close: () => void;
  filter: any;
}

export default function EventTypeSetting({
  highPriorityEvents,
  visible,
  selectedTypes,
  confirm,
  close,
  filter
}: IEventTypeSettingProps) {
  const { formatMessage, isEn } = useLanguage();

  // 情感分类配置
  const sentimentCategoryConfig: {
    key: SentimentCategory;
    labelKey: string;
    icon: string;
  }[] =
    [
      { key: 'positive', labelKey: 'Positive', icon: 'check' },
      { key: 'negative', labelKey: 'Negative', icon: 'close' },
    ];

  // 内部状态：用于编辑过程中的临时选择
  // 初始化为空数组，在 useEffect 中根据 selectedTypes 更新
  const {
    state: localSelectedTypes,
    setState: setLocalSelectedTypes,
    syncStates: syncLocalSelectedTypes,
    revertToPrevious: revertLocalSelectedTypes,
    initStates: initLocalSelectedTypes,
  } = useSyncState<string[]>([]);

  // 当前激活的情感分类 tab
  const [activeCategory, setActiveCategory] =
    useState<SentimentCategory>('positive');

  // 从 highPriorityEvents 中提取所有事件，并按情感分类
  const { allEvents, eventsByCategory } = useMemo(() => {
    const allEvents: IHighPriorityEventItem[] = [];
    const positiveEvents: IHighPriorityEventItem[] = [];
    const negativeEvents: IHighPriorityEventItem[] = [];

    Object.values(highPriorityEvents).forEach((events) => {
      events.forEach((event) => {
        allEvents.push(event);
        // 根据 ratio 判断情感：正数为正面，负数为负面
        const ratio = event.ratio ?? 0;
        if (ratio >= 0) {
          positiveEvents.push(event);
        } else {
          negativeEvents.push(event);
        }
      });
    });

    return {
      allEvents,
      eventsByCategory: {
        positive: positiveEvents,
        negative: negativeEvents,
      },
    };
  }, [highPriorityEvents]);

  // 一级选项：情感分类
  const levelOneOptions = useMemo(() => {
    return sentimentCategoryConfig.map((config) => ({
      key: config.key,
      label: formatMessage({ id: config.labelKey }),
      count: eventsByCategory[config.key].length,
      children: eventsByCategory[config.key].map((e) => e.id),
    }));
  }, [eventsByCategory, formatMessage]);

  // 二级选项：当前激活分类下的事件列表
  const levelTwoOptions = useMemo(() => {
    return eventsByCategory[activeCategory] || [];
  }, [eventsByCategory, activeCategory]);

  // 弹窗打开时同步外部状态到内部状态
  // null 表示全选，需要将所有事件的 id 添加到内部状态
  useEffect(() => {
    if (visible) {
      if (selectedTypes === null) {
        // null 表示全选，初始化为所有事件的 id
        initLocalSelectedTypes(allEvents.map((e) => e.id));
      } else {
        initLocalSelectedTypes(selectedTypes);
      }
    }
  }, [visible, selectedTypes, allEvents]);

  // 一级选中状态
  const { levelOneSelect, levelOneIndeterminate, levelOneIsClear } =
    useMemo(() => {
      const result: {
        levelOneSelect: Record<SentimentCategory, boolean>;
        levelOneIndeterminate: Record<SentimentCategory, boolean>;
        levelOneIsClear: boolean;
      } = {
        levelOneSelect: { positive: false, negative: false },
        levelOneIndeterminate: { positive: false, negative: false },
        levelOneIsClear: false,
      };

      for (const config of sentimentCategoryConfig) {
        const categoryEvents = eventsByCategory[config.key];
        const selectedInCategory = localSelectedTypes.filter((id) =>
          categoryEvents.some((e) => e.id === id),
        );

        if (
          categoryEvents.length === selectedInCategory.length &&
          categoryEvents.length > 0
        ) {
          result.levelOneSelect[config.key] = true;
          result.levelOneIsClear = true;
        } else if (selectedInCategory.length > 0) {
          result.levelOneIndeterminate[config.key] = true;
          result.levelOneIsClear = true;
        }
      }

      return result;
    }, [localSelectedTypes, eventsByCategory]);

  // 二级选中状态
  const levelTwoSelect = useMemo(() => {
    return localSelectedTypes.filter((id) =>
      levelTwoOptions.some((e) => e.id === id),
    );
  }, [localSelectedTypes, levelTwoOptions]);

  const levelTwoIsClear = levelTwoSelect.length > 0;
  const levelTwoIsAll =
    levelTwoOptions.length > 0 &&
    levelTwoSelect.length === levelTwoOptions.length;

  // 处理一级全选/清除
  const handleLevelOneToggleClearBtn = () => {
    if (levelOneIsClear) {
      // 清除所有
      setLocalSelectedTypes([]);
    } else {
      // 全选所有
      setLocalSelectedTypes(allEvents.map((e) => e.id));
    }
  };

  // 处理一级分类勾选
  const handleLevelOneChange = (
    category: SentimentCategory,
    checked: boolean,
  ) => {
    setActiveCategory(category);
    const categoryIds = eventsByCategory[category].map((e) => e.id);

    if (checked) {
      // 添加该分类下所有事件
      setLocalSelectedTypes([
        ...new Set([...localSelectedTypes, ...categoryIds]),
      ]);
    } else {
      // 移除该分类下所有事件
      setLocalSelectedTypes(
        localSelectedTypes.filter((id) => !categoryIds.includes(id)),
      );
    }
    buriedBtnPoint('KMLG_EventTypeSetting_Category', { category, checked });
  };

  // 处理二级全选/清除
  const handleLevelTwoToggleClearBtn = () => {
    const categoryIds = levelTwoOptions.map((e) => e.id);

    if (levelTwoIsClear) {
      // 清除当前分类
      setLocalSelectedTypes(
        localSelectedTypes.filter((id) => !categoryIds.includes(id)),
      );
    } else {
      // 全选当前分类
      setLocalSelectedTypes([
        ...new Set([...localSelectedTypes, ...categoryIds]),
      ]);
    }
  };

  // 处理二级事件勾选
  const handleLevelTwoChange = (selectedIds: string[]) => {
    // 合并其他分类的选中项
    const otherCategoryIds = localSelectedTypes.filter(
      (id) => !levelTwoOptions.some((e) => e.id === id),
    );
    setLocalSelectedTypes([...otherCategoryIds, ...selectedIds]);
  };

  // 关闭弹窗
  const closeWindow = () => {
    close();
    revertLocalSelectedTypes();
  };

  // 确认
  const handleConfirm = () => {
    syncLocalSelectedTypes();
    // 如果选中了所有事件，返回 null 表示全选
    const allEventIds = allEvents.map((e) => e.id);
    const isAllSelected =
      allEventIds.length > 0 &&
      localSelectedTypes.length === allEventIds.length &&
      allEventIds.every((id) => localSelectedTypes.includes(id));

    confirm(isAllSelected ? null : localSelectedTypes);
    close();
    buriedBtnPoint('KMLG_EventTypeSetting_Confirm', {
      count: localSelectedTypes.length,
    });
  };

  // 获取事件名称
  const getEventName = (event: IHighPriorityEventItem) => {
    return isEn
      ? event.title || event.name || event.description || event.id
      : event.title || event.name || event.description_cn || event.id;
  };

  return (
    <Modal
      className="dbo-modal-event-type-setting dbo-modal-event-setting"
      visible={visible}
      caption={formatMessage({ id: 'Show Anomaly Attribution' })}
      onClose={closeWindow}
    >
      <Modal.Body>
        <div className="dbo-event-type-setting">
          <div className="dbo-event-type-setting-bd dbo-event-setting-bd">
            <CardCommon
              contentCard
              empty={allEvents.length === 0}
              emptyHeight={280}
              hasEmptyTitle={false}
            >
              <div className="dbc-region-select-container">
                <div className="dbc-region-select-content-wrap">
                  <div className="dbc-region-select-content-title-wrap">
                    <span>{formatMessage({ id: 'Sentiment Category' })}</span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelOneToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelOneIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    {levelOneOptions.map((item) => (
                      <div
                        key={item.key}
                        onClick={() => setActiveCategory(item.key)}
                        className={`dbc-region-select-checkbox ${activeCategory === item.key ? 'active' : ''}`}
                      >
                        <Checkbox
                          value={levelOneSelect[item.key]}
                          indeterminate={levelOneIndeterminate[item.key]}
                          onChange={(value) =>
                            handleLevelOneChange(item.key, value)
                          }
                        ></Checkbox>
                        <div className="dbc-region-select-checkbox-name-wrap">
                          <span className="dbc-region-select-checkbox-name">
                            {item.label} ({item.count})
                          </span>
                          <Icon type="arrowright" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="dbc-region-select-content-wrap dbc-region-select-content-wrap--right">
                  <div className="dbc-region-select-content-title-wrap">
                    <span className="dbc-region-select-content-country-title">
                      {formatMessage({ id: 'Event' })}
                    </span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelTwoToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelTwoIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    <Checkbox.Group
                      value={levelTwoSelect}
                      onChange={handleLevelTwoChange}
                    >
                      {levelTwoOptions.map((event) => (
                        <Checkbox
                          key={event.id}
                          name={event.id}
                          className="dbc-region-select-checkbox"
                        >
                          <div className="dbc-region-select-checkbox-name-wrap">
                            <span className="dbc-region-select-checkbox-name">
                              {getEventName(event)}
                            </span>
                          </div>
                          <span className="dbc-region-select-checkbox-date">
                            {
                              DBO_TM.timeTitle({
                                start_time: event.start_time || '',
                                end_time: event.end_time || '',
                                date_type: filter?.date_type as ServerTimeType || 'daily',
                              })
                            }
                            {event.ratio !== undefined && (
                              <>
                                <span className="dbc-region-select-checkbox-date-label">
                                  {formatMessage({
                                    id:
                                      event.ratio > 0
                                        ? 'Sentiment increase'
                                        : 'Sentiment decrease',
                                  })}
                                </span>

                                <PercentageItem
                                  percentage={event.ratio}
                                  showIcon={true}
                                />
                              </>
                            )}
                          </span>
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </div>
                </div>
              </div>
            </CardCommon>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={handleConfirm}>
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={closeWindow}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
